import { useState } from "react";
import { listAuditPlans, createAuditPlan } from "@/lib/audit-plan";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { PlusCircle, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { NewAuditPlanDialog } from "./_components/new-audit-plan-dialog";
import { AuditPlanList } from "./_components/audit-plan-list";

export default function AuditPlansPage() {
  const {
    isError,
    isPending,
    isSuccess,
    data: plans,
  } = useQuery({ queryKey: ["auditPlans"], queryFn: listAuditPlans });
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: createAuditPlan,
    onSuccess: () => {
      setIsFormOpen(false);
      toast({
        title: "Success",
        description: "New audit plan created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["auditPlans"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Could not create the audit plan",
        variant: "destructive",
      });
    },
  });

  const { toast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <h1 className="text-3xl font-semibold text-foreground">
          Manage Audit Plans
        </h1>

        <Button
          onClick={() => setIsFormOpen(true)}
          className="w-full sm:w-auto"
        >
          <PlusCircle className="mr-2 h-5 w-5" /> Add New Audit Plan
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit Plan Overview</CardTitle>
          <CardDescription>
            A list of all audits, filterable by year.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPending && (
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span>Loading audit plans...</span>
              </div>
            </div>
          )}

          {isError && (
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2">
                <p>Error occurred while loading audit plans</p>
              </div>
            </div>
          )}

          {isSuccess && <AuditPlanList plans={plans} />}
        </CardContent>
      </Card>

      <NewAuditPlanDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        mutation={createMutation}
      />
    </div>
  );
}
